<script>
  import ImageRef from '#lib/ImageRef.svelte';
  import ZoomableImage from '#lib/ZoomableImage.svelte';
  import EntrySelectorDrawer from '#lib/EntrySelectorDrawer.svelte';
  import { find_closest_aspect_ratio_for_brand } from '#lib/util.js';
  import t from '#lib/i18n.svelte.js';
  import { getContext } from 'svelte';

  const MAX_IMAGES = 6;

  let { 
    /** @type {Array<{width: number, height: number, src: string}>} */
    images = [], 
    /** @type {Array<string|undefined>} */
    images_multiref = $bindable([]),
    /** @type {boolean} */
    is_editing = false,
    /** @type {((index: number) => void)|undefined} */
    onremove,
    /** @type {((entry_ids: string[]) => void)|undefined} */
    onadd,
  } = $props();

  /**
   * Handle adding multiple images from the picker
   * @param {string[]} entry_ids - Array of selected entry IDs
   */
  function handle_add_images(entry_ids) {
    if (onadd) {
      onadd(entry_ids);
    } else {
      // Default behavior: push all to images_multiref (respecting max limit)
      for (const entry_id of entry_ids) {
        if (images_multiref.length >= MAX_IMAGES) break;
        images_multiref.push(entry_id);
      }
    }
  }

  let site_settings = getContext('site_settings');

  let common_aspect_ratio = $derived.by(() => {
    const aspect_ratios = images.flatMap(image => 
      image.width / image.height
    );
    return find_closest_aspect_ratio_for_brand(aspect_ratios, site_settings.aspect_ratios);
  });

  const layouts = {
    // 1x Image
    1: {
      patterns: [
        {
          slots: [{ type: 'landscape', column: [1, 5], row: [1, 3] }],
          priority: 1
        },
        {
          slots: [{ type: 'portrait', column: [2, 4], row: [1, 3] }],
          priority: 2
        },
      ]
    },
    // 2x Images
    2: {
      patterns: [
        {
          // Best for: landscape + landscape
          slots: [
            { type: 'landscape', column: [1, 3], row: [1, 3] },
            { type: 'landscape', column: [3, 5], row: [1, 3] },
          ],
          priority: 1
        },
        {
          // Best for: portrait + landscape
          slots: [
            { type: 'portrait', column: [1, 2], row: [1, 3] },
            { type: 'landscape', column: [2, 5], row: [1, 3] },
          ],
          priority: 2
        },
        {
          // Best for: portrait + portrait
          slots: [
            { type: 'portrait', column: [1, 3], row: [1, 3] },
            { type: 'portrait', column: [3, 5], row: [1, 3] },
          ],
          priority: 3
        },
      ]
    },
    // 3x Images
    3: {
      patterns: [
        {
          // Best for: landscape + portrait + portrait
          slots: [
            { type: 'landscape', column: [1, 3], row: [1, 3] },
            { type: 'portrait', column: [3, 4], row: [1, 3] },
            { type: 'portrait', column: [4, 5], row: [1, 3] },
          ],
          priority: 1
        },
      ]
    },
    // 4x Images
    4: {
      patterns: [
        {
          // Best for: 4x portraits
          slots: [
            { type: 'portrait', column: [1, 2], row: [1, 3] },
            { type: 'portrait', column: [2, 3], row: [1, 3] },
            { type: 'portrait', column: [3, 4], row: [1, 3] },
            { type: 'portrait', column: [4, 5], row: [1, 3] },
          ],
          priority: 1
        },
        {
          // Best for: 3x landscapes and 1x portrait
          slots: [
            { type: 'landscape', column: [1, 3], row: [1, 3] },
            { type: 'portrait', column: [3, 4], row: [1, 3] },
            { type: 'landscape', column: [4, 5], row: [1, 2] },
            { type: 'landscape', column: [4, 5], row: [2, 3] },
          ],
          priority: 2
        },
      ]
    },
    // 5x Images
    5: {
      patterns: [
        {
          // Best for: 2x landscapes and 3x portraits
          slots: [
            { type: 'portrait', column: [1, 2], row: [1, 3] },
            { type: 'portrait', column: [2, 3], row: [1, 3] },
            { type: 'landscape', column: [3, 4], row: [1, 2] },
            { type: 'landscape', column: [3, 4], row: [2, 3] },
            { type: 'portrait', column: [4, 5], row: [1, 3] },
          ],
          priority: 1
        },
        {
          // Best for: 5x landscapes
          slots: [
            { type: 'landscape', column: [1, 3], row: [1, 3] },
            { type: 'landscape', column: [3, 4], row: [1, 2] },
            { type: 'landscape', column: [3, 4], row: [2, 3] },
            { type: 'landscape', column: [4, 5], row: [1, 2] },
            { type: 'landscape', column: [4, 5], row: [2, 3] },
          ],
          priority: 2
        },
      ]
    },
    6: {
      patterns: [
        {
          // Best for: 4x landscapes and 2x portraits
          slots: [
            { type: 'portrait', column: [1, 2], row: [1, 3] },
            { type: 'landscape', column: [2, 3], row: [1, 2] },
            { type: 'landscape', column: [2, 3], row: [2, 3] },
            { type: 'landscape', column: [3, 4], row: [1, 2] },
            { type: 'landscape', column: [3, 4], row: [2, 3] },
            { type: 'portrait', column: [4, 5], row: [1, 3] },
          ],
          priority: 1
        }
      ]
    }
  };

  function find_best_layout(images) {
    const count = images?.length || 1;
    if (!layouts[count]) return null;

    // Get default pattern (highest priority for this count)
    const default_pattern = layouts[count].patterns.sort((a, b) => a.priority - b.priority)[0];

    // If no images or any images are missing dimensions, return default pattern
    if (!images || images.some(img => !img?.width || !img?.height)) {
      return default_pattern.slots;
    }

    // Determine image types
    const image_types = images.map(img => img.width > img.height ? 'landscape' : 'portrait');
    
    // Count each type
    const type_counts = {
      landscape: image_types.filter(t => t === 'landscape').length,
      portrait: image_types.filter(t => t === 'portrait').length
    };

    // Find the best matching pattern
    const patterns = layouts[count].patterns;
    let best_pattern = default_pattern;
    let best_score = -1;

    for (const pattern of patterns) {
      const pattern_counts = {
        landscape: pattern.slots.filter(s => s.type === 'landscape').length,
        portrait: pattern.slots.filter(s => s.type === 'portrait').length
      };

      // Calculate match score based on how well the counts match
      // and the pattern's priority
      let score = 100 - Math.abs(pattern_counts.landscape - type_counts.landscape) 
                     - Math.abs(pattern_counts.portrait - type_counts.portrait)
                     + (1 / pattern.priority); // Higher priority patterns score slightly better

      if (score > best_score) {
        best_score = score;
        best_pattern = pattern;
      }
    }

    return best_pattern.slots;
  }

  let selected_layout = $derived.by(() => {
    // Create a new array that matches images_multiref length by padding with undefined
    const padded_images = [...images];
    if (images_multiref.length > images.length) {
      padded_images.push(...Array(images_multiref.length - images.length).fill(undefined));
    }
    return find_best_layout(padded_images);
  });

  function get_grid_style(index) {
    if (!selected_layout || index >= selected_layout.length) return '';
    const slot = selected_layout[index];
    return `--grid-column: ${slot.column[0]} / ${slot.column[1]}; --grid-row: ${slot.row[0]} / ${slot.row[1]};`;
  }

</script>

<div 
  class='panorama-grid-collage'
  style:--common-aspect-ratio={common_aspect_ratio}
>
  {#if is_editing}
    {#if images_multiref.length > 0}
      {#each images_multiref as image_ref, index (index)}
        <ImageRef 
          {is_editing} 
          bind:image_ref={images_multiref[index]}
          image={images[index]} 
          wrapper_style={get_grid_style(index)}
          onremove={() => onremove?.(index)}
          selected_entry_ids={images_multiref}
          can_remove_empty={images_multiref.length > 1}
        />
      {/each}
    {:else}
      <ImageRef 
        {is_editing} 
        bind:image_ref={images_multiref[0]}
        wrapper_style={get_grid_style(0)}        
        onremove={() => onremove?.(0)}
        multi_select={true}
        onmultiselect={handle_add_images}
      />
    {/if}
    <!-- For now we limit to max 6x images -->
    <!-- TODO: Support layouts with more than 6 images (e.g. with show more button visible in modal/ slideshow) -->
    <!-- We want to show the add button when the array is non-empty AND has no null/undefined values AND is under the limit -->
    {#if images_multiref.length > 0 && images_multiref.length < MAX_IMAGES && !images_multiref.includes(null) && !images_multiref.includes(undefined)}
      <div class='add-image-button'>
        <EntrySelectorDrawer
          entry_type='image'
          excluded_ids={images_multiref.filter(Boolean)}
          label={t('browse_images')}
          icon='image'
          variant='brand encased'
          multi_select={true}
          confirm_label={t('add_selected_entry')}
          confirm_label_plural={t('add_selected_entries', { count: '{{count}}' })}
          onconfirm={handle_add_images}
        />
      </div>
    {/if}
  {:else}
    {#each images as image, index}
      <ZoomableImage 
        {...image} 
        style={get_grid_style(index)}
        sizes='(min-width: 1025px) 1000px, 95vw'
      />
    {/each}
  {/if}
</div>

<style lang='postcss'>
  .panorama-grid-collage {
    --max-image-height: 60vh;
    max-height: var(--max-image-height);
    width: 100%;
    display: grid;
    gap: var(--size-3);
    position: relative;

    /* For Desktop: Grid Collage */
    @media (min-width: 1024px) {
      &:has(:global(:is(.image-ref, .zoomable-image))) {
        min-height: 25cqw;
      }
      max-height: 60vh;
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: repeat(2, 1fr);
      :global(:is(.image-ref, .zoomable-image)) {
        min-height: 0;
        overflow: hidden;
        grid-column: var(--grid-column);
        grid-row: var(--grid-row);
        :global(*:has(> .media-item)), :global(.media-item) {
          width: 100%;
          height: 100%;
        }
      }
      .add-image-button {
        position: absolute;
        right: var(--size-3);
        bottom: var(--size-3);
      }
    }

    /* For Mobile: Carousel Layout */
    @media (max-width: 1023px) {
      grid-template-columns: auto;
      grid-auto-flow: column;
      overflow-x: auto;
      overscroll-behavior-x: contain;
      grid-template-rows: 1fr;
      scroll-behavior: smooth;
      scroll-snap-align: start;
      scroll-snap-type: both mandatory;

      /* Hide scrollbars visually only */
      &::-webkit-scrollbar {
        display: none;
      }
      -ms-overflow-style: none;
      scrollbar-width: none;

      :global(> .image-ref:has(.placeholder-image)) {
        min-width: 50cqw;
      }
      
      /* Special styles for the first image in the gallery */
      :global(:is(.image-ref, .zoomable-image):first-child:has( ~ :is(.image-ref, .zoomable-image))) {
        width: 100%;
        :global(.media-item) {
          width: auto;
          /* Limit width to viewport to ensure scrollability is visible */
          max-width: calc(80vw - var(--page-inline-spacing) * 2);
          height: 100%;
        }
        :global(.media-item:not(.fit-to-frame)) {
          object-fit: cover;
        }
      }

      /* Styles for all zoomable images in the gallery */
      :global(:is(.image-ref, .zoomable-image)) {
        height: calc(var(--max-image-height) * min(var(--common-aspect-ratio), 1));
        :global(.media-item) {
          /* width: auto; */
          /* max-width: none; */
          /* object-fit: contain; */
        }
        :global(.media-item), :global(button:has(.placeholder-image)) {
          height: 100%;
        }
      }

      /* Single fit-to-frame image: auto height to prevent horizontal cropping */
      :global(:is(.image-ref, .zoomable-image):first-child:not(:has(~ :is(.image-ref, .zoomable-image))):has(.fit-to-frame)) {
        height: auto;
        min-height: 0;
        :global(.media-item) {
          width: 100%;
          height: 100%;
        }
      }

      :global(> *) {
        width: auto;
        scroll-snap-stop: normal;
        scroll-snap-align: center;
      }
    }
  }
</style>